import { Link } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { usePageSEO } from "@/lib/seo";

export default function PrivacyPage() {
  usePageSEO({
    title: "Privacybeleid | Stichting FIN",
    description: "Hoe Stichting FIN omgaat met persoonsgegevens en gevoelige informatie van bezoekers, ouders en partners.",
  });

  return (
    <div data-testid="privacy-page">
      <section className="relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-gradient-to-b from-peachSoft/50 to-cream" />
        <div className="container-x pt-16 md:pt-24 pb-14 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra mb-5">Privacybeleid</p>
          <h1 className="font-display text-[clamp(2.4rem,6vw,4.4rem)] leading-[1.05] tracking-tight text-balance">
            Zorgvuldig met uw gegevens.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-inkMuted text-pretty">
            Stichting FIN werkt met mensen in kwetsbare situaties. Wij nemen privacy daarom serieus en gaan bewust om met de gegevens die u met ons deelt.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-3xl space-y-8 text-[16px] leading-[1.75] text-inkMuted text-pretty">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-ink tracking-tight mb-3">Welke gegevens vragen wij bij eerste contact?</h2>
            <p>
              Bij een eerste contact vragen wij alleen wat nodig is om u goed te kunnen bereiken: uw naam, telefoonnummer of e-mailadres, uw gemeente en een korte omschrijving van uw hulpvraag. Meer is niet nodig voor een kennismakingsgesprek.
            </p>
          </div>

          <div className="bg-ivory border border-line rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <Lock size={18} className="text-terra mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-2">Wat wij niet via het formulier ontvangen</p>
                <p className="text-sm text-inkMuted">
                  Wij vragen u nadrukkelijk om via onze contactformulieren <strong className="text-ink">geen BSN, complete dossiers of medische informatie</strong> te delen. Dat soort documenten bespreken we later via een geschikte, veilige route wanneer dat nodig is.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl text-ink tracking-tight mb-3">Waarvoor gebruiken wij uw gegevens?</h2>
            <p>
              Uw gegevens gebruiken wij uitsluitend om contact met u op te nemen, uw vraag te bespreken en waar nodig ondersteuning te bieden. We verstrekken uw gegevens niet aan derden, tenzij dit wettelijk verplicht is of u ons daarvoor uitdrukkelijk toestemming heeft gegeven.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl text-ink tracking-tight mb-3">Bewaren en beveiligen</h2>
            <p>
              We bewaren uw gegevens niet langer dan nodig is voor het doel waarvoor u ons benaderde. Toegang tot uw gegevens is beperkt tot medewerkers die daadwerkelijk bij uw vraag betrokken zijn.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl text-ink tracking-tight mb-3">Uw rechten</h2>
            <p>
              U heeft het recht om uw gegevens in te zien, te laten corrigeren of te laten verwijderen. Ook kunt u uw toestemming voor het gebruik van uw gegevens te allen tijde intrekken. Neem daarvoor contact met ons op via <a href="mailto:info@stichting-fin.nl" className="underline text-terra hover:text-terraDark">info@stichting-fin.nl</a>.
            </p>
          </div>

          <div className="bg-sand rounded-2xl p-6 flex items-start gap-3">
            <Mail size={18} className="text-terra mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="text-ink font-semibold mb-1">Vragen over privacy?</p>
              <p className="text-inkMuted">
                Stuur een e-mail naar <a href="mailto:info@stichting-fin.nl" className="underline text-terra hover:text-terraDark">info@stichting-fin.nl</a>. Voor algemene vragen kunt u ook het <Link to="/contact" className="underline text-terra hover:text-terraDark">contactformulier</Link> gebruiken.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
